/**
 * PaymentSuccess Component
 *
 * Displayed after successful Stripe Checkout.
 * Polls user status until activated, then redirects to the app.
 *
 * Security Fix: Now verifies payment completion by polling user status
 * instead of blindly redirecting (which caused redirect loop when
 * webhook was slow to process).
 *
 * Project: P07 - Stripe Integration
 * Sprint: S03 - Payment Integration
 * Work Unit: WU02 - Payment Flow UI
 * Security Audit Fix: Finding #4 (Post-payment redirect loop)
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';

import { Card, CardBody, Button } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { Brand } from '@enfomentis/shared/config';
import { useAuth } from '@/hooks/useAuth';
import { usersRepository } from '@/repositories/usersRepository';

// =============================================================================
// Constants
// =============================================================================

/** Maximum time to wait for webhook processing (30 seconds) */
const MAX_POLL_TIME_MS = 30000;

/** Interval between status checks (2 seconds) */
const POLL_INTERVAL_MS = 2000;

/** Countdown after activation confirmed (3 seconds) */
const REDIRECT_COUNTDOWN_SECONDS = 3;

// =============================================================================
// Types
// =============================================================================

type ActivationState = 'polling' | 'activated' | 'timeout' | 'error';

// =============================================================================
// Component
// =============================================================================

export function PaymentSuccess(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authState } = useAuth();

  // State
  const [activationState, setActivationState] = useState<ActivationState>('polling');
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);
  const [pollAttempts, setPollAttempts] = useState(0);

  // Extract session_id from URL (for logging/debugging - not currently used for verification)
  const sessionId = searchParams.get('session_id');

  // Poll for user status activation
  const checkUserStatus = useCallback(async (): Promise<boolean> => {
    if (!authState.user?.id) {
      console.log('[PaymentSuccess] No user ID, cannot check status');
      return false;
    }

    try {
      const user = await usersRepository.getById(authState.user.id);
      if (user?.status === 'active') {
        console.log('[PaymentSuccess] User status is active!');
        return true;
      }
      console.log(`[PaymentSuccess] User status is: ${user?.status || 'unknown'}`);
      return false;
    } catch (error) {
      console.error('[PaymentSuccess] Error checking user status:', error);
      return false;
    }
  }, [authState.user?.id]);

  // Polling effect
  useEffect(() => {
    if (activationState !== 'polling') return;

    let pollTimeout: ReturnType<typeof setTimeout>;
    let overallTimeout: ReturnType<typeof setTimeout>;
    let isCancelled = false;

    const poll = async () => {
      if (isCancelled) return;

      const isActive = await checkUserStatus();
      setPollAttempts((prev) => prev + 1);

      if (isActive) {
        setActivationState('activated');
      } else if (!isCancelled) {
        // Schedule next poll
        pollTimeout = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    // Start polling
    poll();

    // Set overall timeout
    overallTimeout = setTimeout(() => {
      isCancelled = true;
      clearTimeout(pollTimeout);
      setActivationState('timeout');
    }, MAX_POLL_TIME_MS);

    return () => {
      isCancelled = true;
      clearTimeout(pollTimeout);
      clearTimeout(overallTimeout);
    };
  }, [activationState, checkUserStatus]);

  // Countdown and redirect after activation
  useEffect(() => {
    if (activationState !== 'activated') return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate('/', { replace: true });
          return 0;
        }
        timeoutId = setTimeout(tick, 1000);
        return prev - 1;
      });
    };

    timeoutId = setTimeout(tick, 1000);

    return () => clearTimeout(timeoutId);
  }, [activationState, navigate]);

  // Handle manual retry
  const handleRetry = () => {
    setPollAttempts(0);
    setActivationState('polling');
  };

  // Handle manual continue (for timeout state)
  const handleContinue = () => {
    navigate('/', { replace: true });
  };

  // Log session ID for debugging
  useEffect(() => {
    if (sessionId) {
      console.log(`[PaymentSuccess] Stripe session ID: ${sessionId}`);
    }
  }, [sessionId]);

  // ==========================================================================
  // Render: Polling state
  // ==========================================================================
  if (activationState === 'polling') {
    return (
      <AppLayout hideFooter>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-6">
              {/* Success Icon */}
              <div className="text-success flex justify-center">
                <CheckCircle className="w-20 h-20" strokeWidth={1.5} />
              </div>

              {/* Success Message */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-text-primary">
                  Payment Successful!
                </h1>
                <p className="text-default-600">
                  Welcome to {Brand.displayName}!
                </p>
              </div>

              {/* Activating Notice */}
              <div className="bg-default-50 rounded-lg p-4">
                <div className="flex items-center justify-center gap-2 text-primary mb-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="font-medium">Activating your account...</span>
                </div>
                <p className="text-sm text-default-500">
                  This usually takes just a few seconds.
                </p>
              </div>

              {/* Poll attempts indicator (subtle) */}
              {pollAttempts > 3 && (
                <p className="text-xs text-default-400">
                  Still processing... ({pollAttempts} checks)
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // ==========================================================================
  // Render: Activated state
  // ==========================================================================
  if (activationState === 'activated') {
    return (
      <AppLayout hideFooter>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-6">
              {/* Success Icon */}
              <div className="text-success flex justify-center">
                <CheckCircle className="w-20 h-20" strokeWidth={1.5} />
              </div>

              {/* Success Message */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-text-primary">
                  You&apos;re All Set!
                </h1>
                <p className="text-default-600">
                  Your {Brand.displayName} subscription is now active.
                </p>
              </div>

              {/* Redirect Countdown */}
              <div className="flex items-center justify-center gap-2 text-primary">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">
                  Redirecting to your dashboard in {countdown}...
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // ==========================================================================
  // Render: Timeout state
  // ==========================================================================
  if (activationState === 'timeout') {
    return (
      <AppLayout hideFooter>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-6">
              {/* Warning Icon */}
              <div className="text-warning flex justify-center">
                <AlertCircle className="w-20 h-20" strokeWidth={1.5} />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-text-primary">
                  Taking Longer Than Expected
                </h1>
                <p className="text-default-600">
                  Your payment was successful, but account activation is taking longer
                  than usual. This can happen during high traffic periods.
                </p>
              </div>

              {/* Options */}
              <div className="bg-default-50 rounded-lg p-4">
                <p className="text-sm text-default-600 mb-4">
                  You can try again or continue to the app. If you see the payment
                  page again, please wait a moment and refresh.
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    color="primary"
                    onPress={handleRetry}
                    className="w-full"
                  >
                    Check Again
                  </Button>
                  <Button
                    variant="bordered"
                    onPress={handleContinue}
                    className="w-full"
                  >
                    Continue to App
                  </Button>
                </div>
              </div>

              {/* Support contact */}
              <p className="text-xs text-default-400">
                If this persists, please contact{' '}
                <a href={`mailto:${Brand.supportEmail}`} className="text-primary">
                  {Brand.supportEmail}
                </a>
              </p>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // ==========================================================================
  // Render: Error state (fallback)
  // ==========================================================================
  return (
    <AppLayout hideFooter>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
          <div className="space-y-6">
            <div className="text-danger flex justify-center">
              <AlertCircle className="w-20 h-20" strokeWidth={1.5} />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-text-primary">
                Something Went Wrong
              </h1>
              <p className="text-default-600">
                We encountered an error. Please try again or contact support.
              </p>
            </div>
            <Button color="primary" onPress={handleRetry}>
              Try Again
            </Button>
          </div>
        </CardBody>
      </Card>
    </AppLayout>
  );
}
